import { jsPDF } from "jspdf";
import { htmlToMarkdown, htmlToFormattedLines, stripHtml, sanitizeFilename } from "@/lib/meeting-export";

export async function copyNoteFormatted(html: string): Promise<boolean> {
  try {
    const blob = new Blob([html], { type: "text/html" });
    const plainBlob = new Blob([stripHtml(html)], { type: "text/plain" });
    await navigator.clipboard.write([
      new ClipboardItem({ "text/html": blob, "text/plain": plainBlob }),
    ]);
    return true;
  } catch {
    try {
      await navigator.clipboard.writeText(stripHtml(html));
      return true;
    } catch {
      return false;
    }
  }
}

export function formatNoteMarkdown(html: string, title: string): string {
  const body = htmlToMarkdown(html);
  if (!body.trim()) return "";
  const lines: string[] = [];
  lines.push(`# ${title}`);
  lines.push("");
  lines.push(body);
  return lines.join("\n").trimEnd() + "\n";
}

export function generateNotePdf(html: string, title: string): void {
  const text = stripHtml(html);
  if (!text) return;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 6;
  let y = margin;

  function checkPageBreak(needed: number) {
    if (y + needed > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
  }

  function addWrappedText(
    t: string,
    fontSize: number,
    opts?: { bold?: boolean; color?: [number, number, number] },
  ) {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
    doc.setTextColor(...(opts?.color ?? ([30, 30, 30] as [number, number, number])));
    const splitLines = doc.splitTextToSize(t, contentWidth);
    for (const line of splitLines) {
      checkPageBreak(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(20, 20, 20);
  const titleLines = doc.splitTextToSize(title, contentWidth);
  for (const line of titleLines) {
    checkPageBreak(8);
    doc.text(line, margin, y);
    y += 8;
  }
  y += 4;

  const formattedLines = htmlToFormattedLines(html);
  for (const line of formattedLines) {
    if (line === "") {
      y += 2;
    } else if (line === line.toUpperCase() && line.length > 0 && !/^\s/.test(line)) {
      y += 2;
      addWrappedText(line, 11, { bold: true });
      y += 1;
    } else if (line.startsWith("  \u2022  ") || /^\s+\d+\.\s/.test(line)) {
      addWrappedText(line, 10);
    } else if (line.startsWith("  > ")) {
      addWrappedText(line, 10, { color: [100, 100, 100] });
    } else {
      addWrappedText(line, 10);
    }
  }

  const filename = sanitizeFilename(title) + " - Notes.pdf";
  doc.save(filename);
}
